import { SearchIcon } from "@heroicons/react/outline";
import { X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";

import { Input } from "../input";
import RotatingTextAnimation from "../RotatingTextAnimation";
import { BasicTooltip } from "../Tooltip";

import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/LoadingSpinner";

type SizeType = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

const SearchBar = forwardRef<
  HTMLInputElement,
  {
    placeholderPhrasesArray?: string[];
    value: string;
    onChange: (value: string) => void;
    onSubmit?: () => void;
    className?: string;
    inputClassName?: string;
    disabled?: boolean;
    clearable?: boolean;
    size?: SizeType;
    isLoading?: boolean;
    onClick?: () => void;
    autoFocus?: boolean;
    tooltip?: string;
    rightElement?: React.ReactNode;
  }
>(
  (
    {
      placeholderPhrasesArray = ["Search..."],
      value,
      onChange,
      onSubmit,
      className = "",
      inputClassName = "",
      disabled = false,
      clearable = true,
      isLoading = false,
      size = "md",
      onClick,
      autoFocus = false,
      tooltip,
      rightElement,
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState(value);

    // Sync internal value with external value
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
    };

    const handleClear = useCallback(() => {
      setInternalValue("");
      onChange("");
    }, [onChange]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit?.();
    };

    const getSizeStyles = () => {
      switch (size) {
        case "xs":
          return "h-8 text-xs border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
        case "sm":
          return "h-10 text-sm border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
        case "lg":
          return "h-14 text-lg border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
        case "xl":
          return "h-16 text-xl border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
        case "2xl":
          return "h-20 text-2xl border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
        case "md":
        default:
          return "h-12 border-0 bg-gray-100 shadow-none focus:border-0 focus:ring-0";
      }
    };

    const getTextSizeClass = () => {
      switch (size) {
        case "xs":
          return "text-xs";
        case "sm":
          return "text-sm";
        case "lg":
          return "text-lg";
        case "xl":
          return "text-xl";
        case "2xl":
          return "text-2xl";
        case "md":
        default:
          return "text-base";
      }
    };

    const searchForm = (
      <form onSubmit={handleSubmit} className={`relative ${className}`}>
        <div className="group relative flex items-center">
          {isLoading ? (
            <div className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400">
              <LoadingSpinner size="4" className="flex" />
            </div>
          ) : (
            <SearchIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
          )}
          <Input
            ref={ref}
            type="text"
            onClick={onClick}
            placeholder=""
            value={internalValue}
            onChange={handleChange}
            className={cn(
              "w-full pl-10",
              rightElement && "pr-36",
              !rightElement && clearable && internalValue && "pr-10",
              !rightElement && !(clearable && internalValue) && "pr-4",
              getSizeStyles(),
              inputClassName,
            )}
            disabled={disabled}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
          />
          {!internalValue && (
            <div className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 transform text-gray-400">
              <RotatingTextAnimation
                phrases={placeholderPhrasesArray}
                interval={2500}
                className={getTextSizeClass()}
              />
            </div>
          )}
          {clearable && internalValue && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                "absolute top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600",
                rightElement ? "right-28" : "right-3",
              )}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {rightElement && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightElement}</div>
          )}
        </div>
      </form>
    );

    if (!tooltip) {
      return searchForm;
    }

    return <BasicTooltip content={tooltip}>{searchForm}</BasicTooltip>;
  },
);

SearchBar.displayName = "SearchBar";

export default SearchBar;
